import { type Dispatch, type SetStateAction, useMemo } from 'react';

import {
  type FireflyTertiaryButtonMenuItem,
  type MultiSelectOption,
  ChakraBox,
  FAIconName,
  FireflyTertiaryButtonMenu,
  FlagIconInput,
  TitleInput,
} from '@equip.health/ui';
import { isEmpty } from 'lodash';

import type { FieldErrorState } from '~/components/maud/patients/note/EditNoteForm';
import {
  fillActivityWith,
  NoteFormDefaultSessionLink,
} from '~/components/maud/patients/note/editNoteForm.util';
import { noteTemplateRevisionsToDropdownOptions } from '~/components/maud/patients/note/noteTemplateRevisionsToDropdownOptions';
import { NoteCommunicationMethod } from '~/lib/constants/AddPatientNoteModal.constants';
import { NO_NOTE_TEMPLATE_OPTION } from '~/lib/constants/notes';
import type { EditNoteFormData } from '~/lib/types/notes/EditNoteFormData';
import { getNoteTypeBasedOnAppointmentType } from '~/lib/util/notes/notes.util';
import type { AppointmentTypeName } from '~/lib/constants/scheduling.constants';

type Props = {
  id: string;
  activityWithOptions: MultiSelectOption[];
  clickedTemplateId: string | null;
  setClickedTemplateId: Dispatch<SetStateAction<string | null>>;
  disableField: boolean;
  fieldErrorReducer: FieldErrorState;
  formData: EditNoteFormData;
  isAddendumModal: boolean;
  isGroupClassNote: boolean;
  isPatientAppointmentsLoading: boolean;
  isSessionListEnabled: boolean;
  isRevisionDataLoading: boolean;
  noteTemplateRevisions: NoteTemplateRevision[];
  onFormChange: () => void;
  patientAppointmentsData: GetAppointmentDetailsResponse;
  patientAppointmentsError: string;
  sessionDetailsList: FireflyTertiaryButtonMenuItem[];
  setFieldValue: (
    key: keyof EditNoteFormData,
    value:
      | boolean
      | string
      | Date
      | MultiSelectOption
      | Array<MultiSelectOption>
      | FireflyTertiaryButtonMenuItem,
  ) => void;
  setIsTemplateSwitching: Dispatch<SetStateAction<boolean>>;
  updateFieldErrorReducer: (param: {
    dateError?: string | null;
    titleError?: string | null;
    typeError?: string | null;
  }) => void;
};

export const EditNoteFormTitleRow = ({
  id,
  activityWithOptions,
  clickedTemplateId,
  setClickedTemplateId,
  disableField,
  fieldErrorReducer,
  formData,
  isAddendumModal,
  isGroupClassNote,
  isPatientAppointmentsLoading,
  isSessionListEnabled,
  isRevisionDataLoading,
  noteTemplateRevisions,
  onFormChange,
  patientAppointmentsData,
  patientAppointmentsError,
  sessionDetailsList,
  setFieldValue,
  setIsTemplateSwitching,
  updateFieldErrorReducer,
}: Props) => {
  const hasNoAppointmentsData = useMemo(() => {
    const sessionDetailsListSansDefault = sessionDetailsList.filter(
      (item) =>
        item.node !== NoteFormDefaultSessionLink.node &&
        item.label !== NoteFormDefaultSessionLink.label,
    );

    return (
      (isEmpty(sessionDetailsListSansDefault) ||
        !isEmpty(patientAppointmentsError)) &&
      !isPatientAppointmentsLoading
    );
  }, [
    sessionDetailsList,
    patientAppointmentsError,
    isPatientAppointmentsLoading,
  ]);

  return (
    <ChakraBox
      alignItems="center"
      display="flex"
      gap="24px"
      height="25px"
      marginBottom="12px"
    >
      <ChakraBox alignItems="center" display="flex" gap="12px" height="25px">
        <TitleInput
          id={`${id}__title`}
          isInvalid={Boolean(fieldErrorReducer.titleError)}
          onChange={(val) => {
            setFieldValue('title', val);
            updateFieldErrorReducer({ titleError: null });
          }}
          placeholder="Enter Title"
          value={formData.title || ''} // this || '' is necessary to ensure an old value that gets cleared is properly set to empty
        />
        <FlagIconInput
          iconAriaLabel="Flag as confidential or sensitive"
          id={`${id}__is-confidential`}
          isDisabled={disableField}
          isSelected={formData.isConfidential}
          onClick={() => {
            setFieldValue('isConfidential', !formData.isConfidential);
            onFormChange();
          }}
          tooltipDescriptionText="Flag as confidential or sensitive"
        />
      </ChakraBox>
      {isSessionListEnabled && (
        <FireflyTertiaryButtonMenu
          aria-label="appointment"
          buttonId={`${id}__appointment`}
          buttonLeftIcon={FAIconName.linkRegular}
          buttonText={
            formData.appointment?.label || NoteFormDefaultSessionLink.node
          }
          isDisabled={
            disableField ||
            isPatientAppointmentsLoading ||
            hasNoAppointmentsData ||
            isGroupClassNote
          }
          isLoading={isPatientAppointmentsLoading}
          onOptionClick={(value) => {
            const selectedOption =
              value !== NoteFormDefaultSessionLink.node
                ? sessionDetailsList.find((item) => item.node === value)
                : undefined;

            setFieldValue('appointment', selectedOption);

            // Fill the other fields with appointment details
            const appointmentDetails = patientAppointmentsData.data.find(
              (e) => e.appointmentExternalId === value,
            );

            if (appointmentDetails) {
              setFieldValue('title', appointmentDetails.appointmentTypeLabel);
              setFieldValue(
                'activityDate',
                new Date(appointmentDetails.appointmentStartDateTime),
              );

              const activityWith = fillActivityWith(
                appointmentDetails,
                activityWithOptions,
              );
              if (!isEmpty(activityWith)) {
                setFieldValue('activity', activityWith);
              }

              const noteType = getNoteTypeBasedOnAppointmentType(
                appointmentDetails?.appointmentType as AppointmentTypeName,
              );
              if (noteType) {
                setFieldValue('type', {
                  label: noteType,
                  value: noteType,
                });
              }

              setFieldValue('medium', {
                label: NoteCommunicationMethod.VIDEO,
                value: NoteCommunicationMethod.VIDEO,
              });
            }

            onFormChange();

            if (updateFieldErrorReducer && value)
              updateFieldErrorReducer({
                typeError: null,
                dateError: null,
                titleError: null,
              });
          }}
          options={sessionDetailsList}
        />
      )}
      {!isGroupClassNote && !isAddendumModal && (
        <FireflyTertiaryButtonMenu
          buttonId={`${id}__template`}
          buttonLeftIcon={FAIconName.fileLinesRegular}
          buttonText={
            formData.noteTemplateRevision?.label ||
            NO_NOTE_TEMPLATE_OPTION.label
          }
          isDisabled={false}
          isLoading={isRevisionDataLoading}
          onOptionClick={(templateId) => {
            if (clickedTemplateId === templateId) return;

            setIsTemplateSwitching(true);
            setClickedTemplateId(templateId);
          }}
          options={noteTemplateRevisionsToDropdownOptions(
            noteTemplateRevisions,
          )}
        />
      )}
    </ChakraBox>
  );
};
